import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon } from './Icon.js';

/**
 * «Hablar con una persona» (Reglamento de IA, art. 35).
 *
 * Muestra los canales oficiales de atencion y permite dejar una solicitud
 * ligada a esta conversacion: quien la atienda ve lo que ya se hablo y no
 * vuelve a preguntar. Nombre y contacto son opcionales y solo se envian con la
 * autorizacion marcada.
 */
export function AtencionHumanaDialog({
  contacto,
  token,
  onClose,
  onRegistrada,
}: {
  /** Canales oficiales (HUMAN_CONTACT en el servidor). */
  contacto: string | null;
  token: string | null;
  onClose: () => void;
  /** Numero de la solicitud, para dejar constancia en la conversacion. */
  onRegistrada: (numero: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [medio, setMedio] = useState('');
  const [motivo, setMotivo] = useState('');
  const [acepto, setAcepto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [numero, setNumero] = useState<string | null>(null);
  const firstRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const datos = nombre.trim() !== '' || medio.trim() !== '';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (enviando) return;
    if (datos && !acepto) {
      setProblem('Para enviar tu nombre o tu contacto, marca la autorización.');
      return;
    }
    setEnviando(true);
    setProblem(null);
    try {
      const response = await fetch('/api/atencion-humana', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token: token ?? undefined,
          nombre: nombre.trim() || undefined,
          contacto: medio.trim() || undefined,
          motivo: motivo.trim() || undefined,
          consentimiento: datos ? acepto : undefined,
        }),
      });
      const body = (await response.json().catch(() => null)) as { numero?: string; error?: string } | null;
      if (!response.ok || !body?.numero) throw new Error(body?.error ?? 'No se pudo registrar la solicitud. Inténtalo de nuevo.');
      setNumero(body.numero);
      onRegistrada(body.numero);
    } catch (error) {
      setProblem((error as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="hdialog__backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="hdialog hdialog--human" role="dialog" aria-modal="true" aria-labelledby="human-title" onSubmit={submit}>
        <header className="hdialog__head">
          <span className="hdialog__icon" aria-hidden="true">
            <Icon name="person" size={22} />
          </span>
          <div>
            <h2 id="human-title">Hablar con una persona</h2>
            <p>Puedes pedir en cualquier momento que te atienda una persona de la universidad.</p>
          </div>
          <button type="button" className="hdialog__close" onClick={onClose} aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </header>

        {contacto ? (
          <div className="human__channels">
            <strong>Canales de atención</strong>
            <span>{contacto}</span>
          </div>
        ) : null}

        {numero ? (
          <div className="human__done" role="status">
            <Icon name="check" size={20} />
            <span>
              Registramos tu solicitud <strong>{numero}</strong>.{' '}
              {token
                ? 'Una persona de la universidad la atenderá con esta conversación, sin volver a preguntarte.'
                : 'Una persona de la universidad la atenderá.'}
            </span>
          </div>
        ) : (
          <>
            <p className="human__lead">O deja tu solicitud y te contactamos:</p>
            <div className="hdialog__grid">
              <label className="hfield hfield--wide">
                <span>
                  ¿En qué necesitas ayuda? <em>(opcional)</em>
                </span>
                <textarea ref={firstRef} value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={1000} />
              </label>
              <label className="hfield">
                <span>
                  Nombre <em>(opcional)</em>
                </span>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" maxLength={120} />
              </label>
              <label className="hfield">
                <span>
                  Correo o teléfono <em>(opcional)</em>
                </span>
                <input value={medio} onChange={(e) => setMedio(e.target.value)} autoComplete="email" maxLength={160} />
              </label>
            </div>

            {datos ? (
              <label className="hconsent">
                <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
                <span>
                  Autorizo a la Universidad Metropolitana a usar mi nombre y mi contacto solo para atender esta solicitud.
                </span>
              </label>
            ) : null}

            {problem ? (
              <p className="hdialog__problem" role="alert">
                {problem}
              </p>
            ) : null}
          </>
        )}

        <footer className="hdialog__foot">
          <button type="button" className="text-btn" onClick={onClose}>
            {numero ? 'Cerrar' : 'Cancelar'}
          </button>
          {numero ? null : (
            <button type="submit" className="hsubmit" disabled={enviando}>
              <Icon name="person" size={18} />
              <span>{enviando ? 'Enviando…' : 'Pedir atención'}</span>
            </button>
          )}
        </footer>
      </form>
    </div>
  );
}
